/**
 * Client-side fingerprinting utility
 * 
 * Uses FingerprintJS to generate unique browser fingerprints,
 * and manages the fingerprint cookie.
 */
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import Cookies from 'js-cookie';

/**
 * Cookie name for storing the fingerprint value
 */
export const FINGERPRINT_COOKIE_NAME = 'fp_id'; // Ensure this matches middleware
const COOKIE_EXPIRATION_DAYS = 365;

/**
 * Generate a browser fingerprint using FingerprintJS
 * @returns Promise that resolves with the fingerprint hash
 */
export const generateFingerprint = async (): Promise<string> => {
    // Initialize the FingerprintJS agent
    const fpPromise = FingerprintJS.load()

    // Get the visitor identifier (fingerprint)
    const fp = await fpPromise
    const result = await fp.get()

    // Return the visitor ID as a fingerprint hash
    return result.visitorId
}

/**
 * Retrieve the fingerprint from browser cookie
 * @returns The fingerprint value or null if not found
 */
export const getFingerprintFromCookie = (): string | null => {
    if (typeof document === 'undefined') return null

    const cookies = document.cookie.split(';')
    for (const cookie of cookies) {
        const [name, value] = cookie.trim().split('=')
        if (name === FINGERPRINT_COOKIE_NAME) {
            return value
        }
    }
    return null
}

/**
 * Set the fingerprint cookie in the browser
 * @param fingerprint The fingerprint value to store
 * @param expirationDays Number of days until the cookie expires
 */
export const setFingerprintCookie = (fingerprint: string, expirationDays = 365): void => {
    if (typeof document === 'undefined') return

    const expirationDate = new Date()
    expirationDate.setDate(expirationDate.getDate() + expirationDays)

    document.cookie = `${FINGERPRINT_COOKIE_NAME}=${fingerprint}; path=/; expires=${expirationDate.toUTCString()}; SameSite=Lax${process.env.NODE_ENV === 'production' ? '; Secure' : ''}`
}

/**
 * Initialize or retrieve the fingerprint
 * @returns Promise that resolves with the fingerprint
 */
export async function ensureFingerprint(): Promise<string> {
    // 1. Check if a valid cookie already exists
    const existingFingerprint = Cookies.get(FINGERPRINT_COOKIE_NAME);
    if (existingFingerprint) {
        return existingFingerprint;
    }

    try {
        const fp = await FingerprintJS.load();
        const result = await fp.get();
        const visitorId = result.visitorId;

        // 3. Store the new fingerprint in a cookie
        Cookies.set(FINGERPRINT_COOKIE_NAME, visitorId, {
            expires: COOKIE_EXPIRATION_DAYS,
            path: '/',
            sameSite: 'lax',
            // secure: process.env.NODE_ENV === 'production' // Set secure in production if applicable
        });

        return visitorId;
    } catch (error) {
        console.error('[Fingerprint] Failed to generate:', error);
        throw error; // Re-throw the error to be caught by FingerprintScript
    }
}